import { isValidDisplayName } from './displayName';
import type { Gender, ProfileDraft } from '../firebase/profile';

/**
 * A product decision, not data hygiene: this app is for readers OVER twelve.
 * Twelve itself is not enough — a twelve-year-old belongs on a parent's
 * account, where somebody consented for them, which is exactly what the
 * managed-child accounts are for.
 *
 * The Firestore rules enforce the same number — see validProfileEdit — and
 * this check exists so a bad value is NAMED rather than surfacing later as an
 * unactionable permission-denied.
 */
export const MIN_AGE = 13;
export const MAX_AGE = 120;
/** School runs 1-12; the curriculum documents are keyed by this number. */
export const MIN_GRADE = 1;
export const MAX_GRADE = 12;

export type ProfileFormValues = {
  name: string;
  /** Raw, as typed: the caller has a text field, not a number. */
  age: string;
  gender: Gender | null;
  /**
   * Where the reader sits in school. OPTIONAL, and optional together: these
   * two are what select a curriculum, and everyone who signed up before
   * curricula existed has neither. Requiring them would lock an existing
   * reader out of editing their own name.
   */
  country?: string | null;
  grade?: string | null;
};

export type ProfileProblem = {
  /** Which field the message belongs under. */
  field: 'name' | 'age' | 'gender' | 'country' | 'grade';
  /** A translation key present in both bundles. */
  key: string;
};

/**
 * Validates what someone typed about themselves, in field order.
 *
 * Returns the FIRST problem with the field it belongs to. One shared error
 * string once rendered under the name box reading "enter a valid age", which
 * is why the field travels with the message rather than being guessed by the
 * screen from the text.
 *
 * Shared by both clients so the age floor cannot drift between them.
 */
export function validateProfileDraft(values: ProfileFormValues): ProfileProblem | null {
  const trimmed = values.name.trim();
  if (!isValidDisplayName(trimmed)) {
    return {
      field: 'name',
      // "Use letters and spaces only" is confusing advice for a blank box.
      key: trimmed ? 'auth.nameStep.invalidError' : 'auth.nameStep.emptyError',
    };
  }

  const age = Number(values.age);
  if (values.age.trim() === '' || !Number.isInteger(age) || age < MIN_AGE || age > MAX_AGE) {
    return { field: 'age', key: 'auth.nameStep.ageInvalid' };
  }

  // Gender is NOT checked here. App Review refused it as required personal
  // information at signup (5.1.1(v), 2026-09-19), and signup does not need it:
  // only a live session does, because sessions are same-gender. It is asked
  // for there — see GenderPrompt — and admitStudent still refuses a pair
  // whose genders are not both known.

  // Schooling is optional, but half of it is not: a grade with no country
  // matches no curriculum, so the pupil would silently have no syllabus
  // instead of being told which half is missing.
  const country = (values.country ?? '').trim();
  const grade = (values.grade ?? '').trim();
  if (grade && !country) {
    return { field: 'country', key: 'auth.nameStep.countryMissing' };
  }
  if (country && !grade) {
    return { field: 'grade', key: 'auth.nameStep.gradeInvalid' };
  }
  if (grade) {
    const n = Number(grade);
    if (!Number.isInteger(n) || n < MIN_GRADE || n > MAX_GRADE) {
      return { field: 'grade', key: 'auth.nameStep.gradeInvalid' };
    }
  }

  return null;
}

/**
 * The values a validated draft carries, parsed once.
 *
 * Only call this after validateProfileDraft has returned null; on an invalid
 * draft the numbers are meaningless.
 */
export function parsedDraftOf(values: ProfileFormValues): ProfileDraft {
  const country = (values.country ?? '').trim().toUpperCase();
  const grade = (values.grade ?? '').trim();
  return {
    displayName: values.name.trim(),
    age: Number(values.age),
    // Left out when unanswered. Defaulting it — this used to write 'male' —
    // is a guess, and a guessed gender is how a woman gets paired with a man.
    ...(values.gender === 'male' || values.gender === 'female' ? { gender: values.gender } : {}),
    // Left OUT rather than written as null when unsaid: the rules accept only
    // a two-letter string and a real grade, so a null would be refused, and
    // 'not said yet' is not the same claim as 'no country'.
    ...(country && grade ? { country, grade: Number(grade) } : {}),
  };
}
